package top.wcpe.allincore.ui.runtime.reactive

import top.wcpe.allincore.ui.runtime.internal.concurrent.SyncLock
import top.wcpe.allincore.ui.runtime.internal.concurrent.newIdentitySet
import top.wcpe.allincore.ui.runtime.internal.concurrent.withLock
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import kotlin.jvm.JvmStatic

/**
 * 反应式可变 List（路线 B）。
 *
 * <p>语义：粗粒度通知。任意 mutate（add/remove/set/clear/...）都会同步重跑所有订阅者，
 * 与 Compose {@code SnapshotStateList}、SwiftUI Array via @Published 对齐。
 * 不做 index 级粒度——index 不是稳定身份，业界共识是不值得为此付出实现复杂度。</p>
 *
 * <p>读路径会通过 {@link ReactiveContext#track(Trackable)} 把当前 Effect 注册为依赖；
 * 写路径不进入追踪上下文，只触发同步通知。</p>
 *
 * <p>线程安全：与 {@link MutableSignal} 同款，subscribers 集合用 [SyncLock] 守护（等价迁移前的
 * synchronized(this)）；backing 用 {@link ArrayList}，多线程写需外部协调（DSL 默认单线程 DslExecutor
 * 已经协调）。</p>
 *
 * <p>实现 {@link List} 接口契约，与普通 {@link ArrayList} 在 {@code equals} / {@code hashCode}
 * / 序列化（如 SnakeYAML）等方面完全互通。</p>
 */
@Suppress("CyclomaticComplexMethod", "LongMethod", "TooManyFunctions")
class ReactiveList(private val backing: ArrayList<Any> = ArrayList()) :
    MutableList<Any>,
    Trackable {

    companion object {
        private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.reactive.ReactiveList")

        /** 空列表构造。 */
        @JvmStatic
        fun empty(): ReactiveList = ReactiveList()
    }

    /** subscribers 集合互斥锁（等价迁移前的 this 监视器）。 */
    private val lock = SyncLock()

    // 典型场景下订阅者较少（一个反应式 List 一般只服务于个位数 Effect）
    private val subscribers: MutableSet<Subscriber> = newIdentitySet()

    /** 用现有 List 构造；浅拷贝以避免外部继续 mutate 原 list 时绕过通知。 */
    constructor(source: List<*>) : this(ArrayList(source.map { it as Any }))

    // ===== Trackable =====

    override fun subscribe(subscriber: Subscriber) {
        lock.withLock {
            subscribers.add(subscriber)
        }
    }

    override fun unsubscribe(subscriber: Subscriber) {
        lock.withLock {
            subscribers.remove(subscriber)
        }
    }

    /** 通知所有订阅者；快照副本避免回调内增删订阅造成并发修改。 */
    private fun notifyChanged() {
        val snapshot = subscribers.toList()
        // 处于批次中：仅入队，留待最外层批次出口统一 flush 去重
        if (BatchScope.inBatch()) {
            BatchScope.enqueue(snapshot)
            return
        }
        // T2-5 隔离：单个订阅者 onChange 抛错不阻断其余订阅者；记录首个异常，通知完全部后重抛
        // （与 BatchScope.flush 一致），既防订阅者互相饿死，又保留「异常传播到写入点」的 fail-fast 契约；
        // 客户端不崩由 render / 输入 / tick 入口安全网兜底。不改变快照复制 / 批处理逻辑。
        var firstError: RuntimeException? = null
        for (s in snapshot) {
            try {
                s.onChange()
            } catch (ex: RuntimeException) {
                if (firstError == null) firstError = ex
                LOGGER.error(
                    "[ui] ReactiveList 通知订阅者失败已隔离，继续通知其余订阅者；订阅者=${s::class.simpleName}",
                    ex,
                )
            }
        }
        if (firstError != null) throw firstError
    }

    // ===== List 读 =====

    override val size: Int
        get() {
            ReactiveContext.track(this)
            return backing.size
        }

    override fun isEmpty(): Boolean {
        ReactiveContext.track(this)
        return backing.isEmpty()
    }

    override fun contains(element: Any): Boolean {
        ReactiveContext.track(this)
        return backing.contains(element)
    }

    /**
     * 返回基于当前快照的迭代器。
     *
     * <p>实现刻意每次拷贝一份 {@code new ArrayList<>(backing)}：把"读时 track"与"迭代行为"
     * 解耦——track 只在调用 {@code iterator()} 这一刻发生一次，后续在迭代器上的取值不再叠加
     * 反应式依赖，也不会在 mutate 时抛 ConcurrentModificationException。</p>
     *
     * <p>性能注意：高频迭代会产生显著的快照拷贝 GC 压力，调用方应在循环外取一次结果，
     * 而不是在每次循环里都重新调用 {@code iterator()}。</p>
     */
    override fun iterator(): MutableIterator<Any> {
        ReactiveContext.track(this)
        return ArrayList(backing).iterator()
    }

    fun toArray(): Array<Any> {
        ReactiveContext.track(this)
        return backing.toTypedArray()
    }

    override fun containsAll(elements: Collection<Any>): Boolean {
        ReactiveContext.track(this)
        return backing.containsAll(elements)
    }

    override fun get(index: Int): Any {
        ReactiveContext.track(this)
        return backing[index]
    }

    override fun indexOf(element: Any): Int {
        ReactiveContext.track(this)
        return backing.indexOf(element)
    }

    override fun lastIndexOf(element: Any): Int {
        ReactiveContext.track(this)
        return backing.lastIndexOf(element)
    }

    /**
     * 返回基于当前快照的双向迭代器。
     *
     * <p>语义与 {@link #iterator()} 一致：每次返回 {@code new ArrayList<>(backing)} 的快照副本，
     * 把"读时 track"与"迭代行为"解耦——track 只发生在本次调用，迭代器自身的 next/previous
     * 不再触发反应式依赖。</p>
     *
     * <p>性能注意：高频迭代会产生快照拷贝 GC 压力，调用方应在循环外取一次结果。</p>
     */
    override fun listIterator(): MutableListIterator<Any> {
        ReactiveContext.track(this)
        return ArrayList(backing).listIterator()
    }

    /**
     * 返回基于当前快照、从指定位置开始的双向迭代器。
     *
     * <p>同 {@link #listIterator()}：每次复制一份新的快照，迭代行为不再额外 track。</p>
     */
    override fun listIterator(index: Int): MutableListIterator<Any> {
        ReactiveContext.track(this)
        return ArrayList(backing).listIterator(index)
    }

    /**
     * 返回基于当前快照的子列表副本（独立 ArrayList，与本反应式列表不再联动）。
     *
     * <p>实现刻意每次返回 {@code new ArrayList<>(backing.subList(...))}：把"读时 track"
     * 与"子列表行为"解耦——track 只在本次调用一次，返回的子列表自身不会再产生反应式依赖，
     * 也不会因为 mutate 反应式列表而抛 ConcurrentModificationException。</p>
     *
     * <p>性能注意：高频 subList 会产生快照拷贝 GC 压力，调用方应在循环外取一次结果。</p>
     */
    override fun subList(fromIndex: Int, toIndex: Int): MutableList<Any> {
        ReactiveContext.track(this)
        return ArrayList(backing.subList(fromIndex, toIndex))
    }

    // ===== List 写 =====

    override fun add(element: Any): Boolean {
        val r = backing.add(element)
        notifyChanged()
        return r
    }

    override fun add(index: Int, element: Any) {
        backing.add(index, element)
        notifyChanged()
    }

    override fun remove(element: Any): Boolean {
        val r = backing.remove(element)
        if (r) notifyChanged()
        return r
    }

    override fun removeAt(index: Int): Any {
        val r = backing.removeAt(index)
        notifyChanged()
        return r
    }

    override fun set(index: Int, element: Any): Any {
        val r = backing.set(index, element)
        notifyChanged()
        return r
    }

    override fun addAll(elements: Collection<Any>): Boolean {
        val r = backing.addAll(elements)
        if (r) notifyChanged()
        return r
    }

    override fun addAll(index: Int, elements: Collection<Any>): Boolean {
        val r = backing.addAll(index, elements)
        if (r) notifyChanged()
        return r
    }

    override fun removeAll(elements: Collection<Any>): Boolean {
        val r = backing.removeAll(elements)
        if (r) notifyChanged()
        return r
    }

    override fun retainAll(elements: Collection<Any>): Boolean {
        val r = backing.retainAll(elements)
        if (r) notifyChanged()
        return r
    }

    override fun clear() {
        if (backing.isEmpty()) return
        backing.clear()
        notifyChanged()
    }

    // ===== equals / hashCode / toString：委托 List 接口契约 =====

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        // 不 track —— equals 通常用于断言或外部比较，不应建立反应式依赖
        if (other !is List<*>) return false
        return backing == other
    }

    override fun hashCode(): Int = backing.hashCode()

    override fun toString(): String = backing.toString()
}
